import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

// Admin v2 · V4 — organized by decision, not by data source. Five primary
// pages answer the founder questions; everything else (including B2B, parked
// while the focus is B2C) lives under More.
const primaryNav = [
  { path: '/', label: 'Overview', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { path: '/users', label: 'Students', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { path: '/funnel', label: 'Funnel', icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12' },
  { path: '/retention', label: 'Retention', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  { path: '/revenue', label: 'Revenue', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

const moreNav = [
  { path: '/learning', label: 'Learning', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  { path: '/content', label: 'Content', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/cognitive-mastery', label: 'Cog. Mastery', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { path: '/focus-learning', label: 'Focus Learning', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  { path: '/deletion-funnel', label: 'Churn / Deletes', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
  { path: '/coaching-centers', label: 'Centers', icon: 'M21 12l-9-9-9 9M5 10.5V21h14V10.5' },
  { path: '/batches', label: 'Batches', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { path: '/teachers', label: 'Teachers', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
];

function NavIcon({ d }) {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  );
}

function NavItems({ items, collapsed }) {
  return items.map((item) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg transition-colors ${collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2'} ${
          isActive
            ? 'bg-white/10 text-white'
            : 'text-white/55 hover:bg-white/5 hover:text-white/85'
        }`
      }
    >
      <NavIcon d={item.icon} />
      {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
    </NavLink>
  ));
}

const COLLAPSE_KEY = 'jeevibe_admin_sidebar_collapsed';
function readCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  // More stays collapsed unless the current page lives inside it — the active
  // item must never be invisible.
  const onMorePage = moreNav.some((i) => location.pathname.startsWith(i.path));
  const [moreOpen, setMoreOpen] = useState(false);
  const showMore = moreOpen || onMorePage;
  // Collapsed = icon rail (the Students table is wide; the rail gives it
  // ~180px back). Remembered per browser.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* private mode */ }
      return !c;
    });
  };

  return (
    <div className="min-h-screen flex bg-teacher-bg text-teacher-text-primary">
      {/* Sidebar — dark, matches v2 teacher portal */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} shrink-0 bg-teacher-sidebar text-white flex flex-col overflow-y-auto transition-[width] duration-150`}>
        {/* Logo + collapse toggle */}
        <div className={`${collapsed ? 'px-0 py-4 flex flex-col items-center gap-3' : 'px-5 py-5 flex items-start justify-between'} border-b border-white/10`}>
          <div className={collapsed ? 'text-center' : ''}>
            <div className="text-lg font-semibold leading-tight tracking-tight">
              <span className="bg-brand-gradient bg-clip-text text-transparent">{collapsed ? 'JV' : 'JEE'}</span>
              {!collapsed && <span className="bg-brand-gradient bg-clip-text text-transparent">Vibe</span>}
            </div>
            {!collapsed && <div className="text-[11px] text-white/40 mt-0.5">Admin Portal</div>}
          </div>
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="p-1 rounded-md text-white/45 hover:text-white/85 hover:bg-white/5 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d={collapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7m8 14l-7-7 7-7'} />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 ${collapsed ? 'p-2' : 'p-4'} space-y-1 overflow-y-auto`}>
          <NavItems items={primaryNav} collapsed={collapsed} />
          <div className="pt-3 mt-3 border-t border-white/10">
            {collapsed ? (
              // No label to hide behind, so the rail shows More's items directly.
              <NavItems items={moreNav} collapsed />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setMoreOpen(!showMore)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-white/45 hover:text-white/75 hover:bg-white/5 transition-colors"
                >
                  <NavIcon d="M12 12h.01M5 12h.01M19 12h.01" />
                  <span className="text-sm font-medium">More</span>
                  <span className="ml-auto text-xs">{showMore ? '▾' : '▸'}</span>
                </button>
                {showMore && <NavItems items={moreNav} />}
              </>
            )}
          </div>
        </nav>

        {/* User info */}
        <div className={`${collapsed ? 'px-2 py-3 flex flex-col items-center gap-2' : 'px-5 py-4'} border-t border-white/10`}>
          <div className={`flex items-center gap-3 ${collapsed ? '' : 'mb-3'}`} title={collapsed ? `${user?.displayName || 'Admin'} · ${user?.email || ''}` : undefined}>
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName}
                className="w-9 h-9 rounded-full"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-white/85 text-sm font-medium">
                  {user?.email?.[0]?.toUpperCase()}
                </span>
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/90 truncate">
                  {user?.displayName || 'Admin'}
                </p>
                <p className="text-xs text-white/45 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            title={collapsed ? 'Sign out' : undefined}
            className={`text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors ${collapsed ? 'p-1.5' : 'w-full px-3 py-1.5 text-left'}`}
          >
            {collapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            ) : 'Sign out'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
